import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../../core/network/api.service';
import { LocalStorageService } from '../../../core/storage/local-storage.service';
import { ApiEndpoints } from '../../../core/config/api.endpoints';
import { User, AuthResponse } from '../models/auth.models';

@Injectable({
  providedIn: 'root'
})
export class AuthRepository {
  private api = inject(ApiService);
  private localStorage = inject(LocalStorageService);

  async login(email: string, password: string): Promise<AuthResponse> {
    return firstValueFrom(
      this.api.post<AuthResponse>(ApiEndpoints.login, { email, password })
    );
  }

  async register(email: string, password: string, displayName: string): Promise<AuthResponse> {
    return firstValueFrom(
      this.api.post<AuthResponse>(ApiEndpoints.register, { email, password, displayName })
    );
  }

  async getCurrentUser(userId: string): Promise<User> {
    return firstValueFrom(
      this.api.get<User>(ApiEndpoints.userById(userId))
    );
  }

  async deleteAccount(userId: string): Promise<void> {
    await firstValueFrom(
      this.api.delete<void>(ApiEndpoints.userById(userId))
    );
  }

  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    return firstValueFrom(
      this.api.post<AuthResponse>(ApiEndpoints.refreshToken, { refreshToken })
    );
  }

  async logout(): Promise<void> {
    await firstValueFrom(
      this.api.post<void>(ApiEndpoints.logout, null)
    );
  }

  async verifyEmail(email: string, code: string): Promise<AuthResponse> {
    return firstValueFrom(
      this.api.post<AuthResponse>(ApiEndpoints.verifyEmail, { email, code })
    );
  }

  async resendOtp(email: string): Promise<void> {
    await firstValueFrom(
      this.api.post<void>(ApiEndpoints.resendOtp, { email })
    );
  }

  async requestPasswordReset(email: string): Promise<void> {
    await firstValueFrom(
      this.api.post<void>(ApiEndpoints.forgotPassword, { email })
    );
  }

  async resetPassword(email: string, code: string, newPassword: string): Promise<void> {
    await firstValueFrom(
      this.api.post<void>(ApiEndpoints.resetPassword, { email, code, newPassword })
    );
  }

  /* 
   * Storage helpers mapping to local_storage
   */
  saveTokens(accessToken: string, refreshToken: string, userId: string): void {
    this.localStorage.setString('auth_token', accessToken);
    this.localStorage.setString('auth_refresh_token', refreshToken);
    this.localStorage.setUserId(userId);
  }

  getRefreshToken(): string | null {
    return this.localStorage.getString('auth_refresh_token');
  }

  clearTokens(): void {
    this.localStorage.clearToken();
    this.localStorage.remove('auth_refresh_token');
    this.localStorage.clearUserId();
  }
}
